import { type FC } from "react";
import { Button, Dropdown, Tooltip, type FormInstance } from "antd";
import { ArrowLeftOutlined, DownOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { formatTranslation } from "../../shared/utils/translationUtils";
import { getCancelButtonLabel, getNextButtonLabel } from "./utils";
import type { StepItemType } from "./types";
import TrailModal from "../../shared/layout/Modal";
import type { monitorModaldata } from "../../shared/layout/types";
import { TrialReportDrawer } from "./TrialReportDrawer";
import AgOperationDataLoader from "./SummaryComponent/AgOperationDataLoader";
import PrescriptionExporterMfeLauncher from "../../shared/components/ConnectFiles/PrescriptionExporterModal";
import i18n from "../../i18n";

export interface SummaryFooterProps {
  current: number;
  goToSummary: boolean;
  prev: () => void;
  form: FormInstance<any>;
  onFinish: (
    data: any,
    isBackButton?: boolean,
    options?: { skipNavigateOnSummary?: boolean }
  ) => Promise<void>;
  steps: StepItemType[];
  menuItems: Array<{ label: string; key: string }>;
  handleMenuClick: (e: any) => void;
  trialInfo: any;
  handleViewTrialReport: (trialId: string) => Promise<void>;
  enableNewWorkorderExport: boolean;
  open: boolean;
  handleCancel: () => void;
  handleOk: (data: monitorModaldata, fielFormdData: any) => void;
  reportDrawerKey: number;
  drawerVisible: boolean;
  hideDrawer: () => void;
  generateReport: (
    trialId: string[],
    fieldIds: string[]
  ) => Promise<{ uuid: string; reportId: string }>;
  agLoaderOpen: boolean;
  hideAgLoader: () => void;
  agOperationIds: string[];
  errorMessage: string;
}

const SummaryFooter: FC<SummaryFooterProps> = ({
  current,
  goToSummary,
  prev,
  form,
  onFinish,
  steps,
  menuItems,
  handleMenuClick,
  trialInfo,
  handleViewTrialReport,
  enableNewWorkorderExport,
  open,
  handleCancel,
  handleOk,
  reportDrawerKey,
  drawerVisible,
  hideDrawer,
  generateReport,
  agLoaderOpen,
  hideAgLoader,
  agOperationIds,
  errorMessage,
}) => {
  const { t } = useTranslation();

  return (
    <div className="footer-main">
      <div>
        <Tooltip
          title={
            goToSummary
              ? formatTranslation(t("trials.editDisabledCompleted"))
              : undefined
          }
        >
          <span className="back-button-wrapper">
            <Button
              icon={<ArrowLeftOutlined />}
              size="large"
              className="back-button"
              disabled={goToSummary}
              onClick={
                current > 0
                  ? () => {
                      prev();
                    }
                  : undefined
              }
            >
              {getCancelButtonLabel(current, t)}
            </Button>
          </span>
        </Tooltip>
      </div>
      <div className="bottom-sec-btn bottom-sec-btn--gap">
        <Button
          className="save-btn draft-btn"
          size="large"
          onClick={() => {
            // FIX: void the Promise returned by onFinish to satisfy
            // no-floating-promises — onClick cannot be async here.
            void onFinish(form);
          }}
          data-testid="save-btn"
        >
          {formatTranslation(t("trials.save"))}
        </Button>
      </div>
      <div className="bottom-sec-btn">
        <Dropdown.Button
          menu={{ items: menuItems, onClick: handleMenuClick }}
          onClick={() => {
            if (trialInfo?.id) {
              // local fix: void the returned Promise to satisfy no-floating-promises
              void handleViewTrialReport(trialInfo.id);
            } else {
              console.error("Cannot view trial report: trial ID is undefined");
            }
          }}
          size="large"
          className="save-btn"
          icon={<DownOutlined size={5} />}
        >
          {getNextButtonLabel(current, t, formatTranslation, steps)}
        </Dropdown.Button>
      </div>

      {/* main: feature-flagged prescription exporter (new work order export) */}
      {enableNewWorkorderExport &&
        trialInfo?.id &&
        open &&
        agOperationIds.length > 0 &&
        errorMessage === "" && (
          <PrescriptionExporterMfeLauncher
            isOpen={open}
            onClose={handleCancel}
            agOperationIds={agOperationIds}
            userLanguage={i18n.language}
          />
        )}
      {enableNewWorkorderExport && trialInfo?.id && agLoaderOpen && (
        <AgOperationDataLoader
          open={agLoaderOpen}
          onClose={hideAgLoader}
          errorMessage={errorMessage}
        />
      )}
      {/* main: legacy modal shown when feature flag is off */}
      {!enableNewWorkorderExport && trialInfo?.id && open && (
        <TrailModal
          open={open}
          handleOk={handleOk}
          handleCancel={handleCancel}
          fieldId={trialInfo?.field_ids[0]}
          trialId={trialInfo?.id}
        />
      )}

      {/* local fix: reportDrawerKey forces full remount to clear stale state */}
      <TrialReportDrawer
        key={reportDrawerKey}
        open={drawerVisible}
        onClose={hideDrawer}
        trialId={trialInfo?.id}
        fieldIds={trialInfo?.field_ids}
        onGenerateReport={generateReport}
        propertyId={trialInfo?.property_id}
        noOfFields={trialInfo?.field_ids?.length}
        noOfTrails={trialInfo?.field_ids?.length}
      />
    </div>
  );
};

export default SummaryFooter;
